import React from 'react';
import { createRoot } from 'react-dom/client';
import {
  Route,
  Routes,
  BrowserRouter as Router,
  Navigate,
} from 'react-router-dom';
import App from './app/App';
import store from './app/store';
import { Provider } from 'react-redux';
import * as serviceWorker from './serviceWorker';
import CssBaseline from '@mui/material/CssBaseline';

import PrivateRoute from './components/PrivateRoute';
import setupAxiosInterceptor from './api/setupAxiosInterceptor';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from 'lib/react-query';

import CustomThemeProvider from './app/CustomThemeProvider';
import SpectrasWebSocketServer from './features/spectrasWebSocketServer/SpectrasWebsocketServer';
import Login from './features/login/Login';
import Wall from './features/walls/Wall';
import UserManagementPage from './features/userManagement/UserManagementPage';
import AnalyzerSnapshotsPage from './features/analyzers/AnalyerSnapshotsPage';
import ManageWallsPage from './features/walls/ManageWallsPage';
import WallOrganizer from './features/wallOrganizer/WallOrganizer';
import AlarmLogPage from './features/alarmLog/AlarmLogPage';
import SnapshotAlarmSetupPage from './features/snapshotAlarmSetup/SnapshotAlarmSetupPage';
import TraceHistoryPage from './features/traceHistory/TraceHistoryPage';
import ConsolePage from './features/console/ConsolePage';
import SystemPage from './features/systemInfo/SystemPage';
import CreateLicensesPage from './features/createLicenses/CreateLicensesPage';
import CreateServerLicensesPage from 'features/createLicenses/CreateServerLicensesPage';
import UserSettingsPage from './features/auth/UserSettingsPage';

import { StyledEngineProvider } from '@mui/material/styles';

import { AnalyzerPage } from './features/systemInfo/AnalyzerPage';
import ManageLocationsPage from './features/locations/ManageLocationsPage';
import ManageWeatherWallsPage from './features/weatherWalls/ManageWeatherWallsPage';
import WeatherWallPage from './features/weatherWalls/WeatherWallPage';
import ManageDishesPage from './features/dishes/ManageDishesPage';
import WeatherAlertManagementPage from './features/weatherAlerts/WeatherAlertManagementPage';
import MarkerHistoryPage from 'features/markerHistory/MarkerHistoryPage';
import WeatherReflectionPage from 'features/weatherReflection/WeatherReflectionPage';
import ForecastReflectionPage from 'features/weatherReflection/ForecastReflectionPage';
import ShareWallsSelectionPage from 'features/walls/ShareWallsSelectionPage';
import InstrumentsPage from 'features/analyzerDataSettings/InstrumentsPage';
import ShareWeatherWallsSelectionPage from 'features/weatherWalls/ShareWeatherWallsSelectionPage';
import TimeWarningLogPage from 'features/timeWarningLog/TimeWarningLogPage';
import DetailedSnapshotView from 'features/detailedSnapshotView/DetailedSnapshotView';

import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import WeatherDetailsView from 'features/weatherHistoryView/WeatherDetailsView';
import AlarmWallHistory from './features/alarmWall/AlarmWallHistory';
import AlarmWallLive from './features/alarmWall/AlarmWallLive';
import SystemLogPage from 'features/systemLog/SystemLogPage';
import SystemOverviewPage from './features/system-overview/SystemOverviewPage';
import SatellitePage from './features/satellites/SatellitePage';
import TrendlineView from 'features/trendline/TrendlineView';
import './index.css';
import ExternalLinkSetup from 'features/externalLinks/ExternalLinkSetup';

setupAxiosInterceptor(store);

const container = document.getElementById('root');
const root = createRoot(container!);

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ReactQueryDevtools initialIsOpen={false} />
      <Router>
        <StyledEngineProvider injectFirst>
          <Provider store={store}>
            <CustomThemeProvider>
              <CssBaseline />
              <SpectrasWebSocketServer />
              <Routes>
                <Route path='/login' element={<Login />} />
                <Route
                  path='/'
                  element={
                    <PrivateRoute>
                      <App />
                    </PrivateRoute>
                  }
                />
                <Route
                  path='/wall/:wallId'
                  element={
                    <PrivateRoute>
                      <Wall />
                    </PrivateRoute>
                  }
                />
                <Route
                  path='detailed-view/:wallId/:analyzerId/:snapshotId'
                  element={
                    <PrivateRoute>
                      <DetailedSnapshotView />
                    </PrivateRoute>
                  }
                />
                <Route
                  path='/instruments'
                  element={
                    <PrivateRoute>
                      <InstrumentsPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path='/user-management'
                  element={
                    <PrivateRoute>
                      <UserManagementPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path='/snapshots'
                  element={
                    <PrivateRoute>
                      <AnalyzerSnapshotsPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path='/walls'
                  element={
                    <PrivateRoute>
                      <ManageWallsPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path='/share'
                  element={
                    <PrivateRoute>
                      <ShareWallsSelectionPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path='/weathershare'
                  element={
                    <PrivateRoute>
                      <ShareWeatherWallsSelectionPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path='/edit-wall/:wallId'
                  element={
                    <PrivateRoute>
                      <WallOrganizer />
                    </PrivateRoute>
                  }
                />
                <Route
                  path='/system-log'
                  element={
                    <PrivateRoute>
                      <SystemLogPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path='/time-warning-log'
                  element={
                    <PrivateRoute>
                      <TimeWarningLogPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path='/alarm-log'
                  element={
                    <PrivateRoute>
                      <AlarmLogPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path='/snapshot-alarm-setup'
                  element={
                    <PrivateRoute>
                      <SnapshotAlarmSetupPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path='/trendline/:snapshotId'
                  element={
                    <PrivateRoute>
                      <TrendlineView />
                    </PrivateRoute>
                  }
                />
                <Route
                  path='/trace-history'
                  element={
                    <PrivateRoute>
                      <TraceHistoryPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path='/marker-history'
                  element={
                    <PrivateRoute>
                      <MarkerHistoryPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path='/console'
                  element={
                    <PrivateRoute>
                      <ConsolePage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path='/external-link-setup'
                  element={
                    <PrivateRoute>
                      <ExternalLinkSetup />
                    </PrivateRoute>
                  }
                />
                <Route
                  path='/system-info'
                  element={
                    <PrivateRoute>
                      <SystemPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path='/create-licenses'
                  element={
                    <PrivateRoute>
                      <CreateLicensesPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path='/create-server-licenses'
                  element={
                    <PrivateRoute>
                      <CreateServerLicensesPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path='/user-settings'
                  element={
                    <PrivateRoute>
                      <UserSettingsPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path='/analyzers/:id'
                  element={
                    <PrivateRoute>
                      <AnalyzerPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path='/locations'
                  element={
                    <PrivateRoute>
                      <ManageLocationsPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path='/weatherwalls'
                  element={
                    <PrivateRoute>
                      <ManageWeatherWallsPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path='/weather-wall/:wallId'
                  element={
                    <PrivateRoute>
                      <WeatherWallPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path='/weather-window/:windowId'
                  element={
                    <PrivateRoute>
                      <WeatherDetailsView />
                    </PrivateRoute>
                  }
                />
                <Route
                  path='/edit-weather-wall/:wallId'
                  element={
                    <PrivateRoute>
                      <WeatherWallPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path='/dishes'
                  element={
                    <PrivateRoute>
                      <ManageDishesPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path='/satellites'
                  element={
                    <PrivateRoute>
                      <SatellitePage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path='/weatheralerts'
                  element={
                    <PrivateRoute>
                      <WeatherAlertManagementPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path='/reflection'
                  element={
                    <PrivateRoute>
                      <WeatherReflectionPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path='/forecast-reflection'
                  element={
                    <PrivateRoute>
                      <ForecastReflectionPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path='/alarm-wall-history'
                  element={
                    <PrivateRoute>
                      <AlarmWallHistory />
                    </PrivateRoute>
                  }
                />
                <Route
                  path='/alarm-wall-live'
                  element={
                    <PrivateRoute>
                      <AlarmWallLive />
                    </PrivateRoute>
                  }
                />

                <Route
                  path='/system-overview'
                  element={
                    <PrivateRoute>
                      <SystemOverviewPage />
                    </PrivateRoute>
                  }
                />
                <Route path='*' element={<Navigate to='/login' />} />
              </Routes>
            </CustomThemeProvider>
          </Provider>
        </StyledEngineProvider>
      </Router>
    </QueryClientProvider>
  </React.StrictMode>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
